import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  BookOpen, 
  Download, 
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Wallet,
  CreditCard,
  Building2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  MoreHorizontal,
  RotateCcw,
  Trash2,
  Pencil,
  ChevronDown,
  ChevronRight,
  XCircle,
  Search,
  X,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'APPROVED', label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid', color: 'bg-violet-100 text-violet-700' },
  { value: 'PAID', label: 'Fully Paid', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'REJECTED', label: 'Rejected', color: 'bg-red-100 text-red-700' }
];

// Generate date presets
const getDatePresets = (selectedFY) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  // Parse FY
  let fyStartYear = currentYear;
  if (selectedFY) {
    fyStartYear = parseInt(selectedFY.split('-')[0]);
  }
  
  // Generate month options for FY
  const fyMonths = [];
  for (let i = 0; i < 12; i++) {
    const monthNum = (3 + i) % 12 + 1; // April=4 to March=3
    const year = i < 9 ? fyStartYear : fyStartYear + 1;
    const monthDate = new Date(year, monthNum - 1, 1);
    fyMonths.push({
      value: `month-${year}-${String(monthNum).padStart(2, '0')}`,
      label: monthDate.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }
  
  return {
    presets: [
      { value: 'current-month', label: 'Current Month' },
      { value: 'previous-month', label: 'Previous Month' },
      { value: 'current-quarter', label: 'Current Quarter' },
      { value: 'previous-quarter', label: 'Previous Quarter' },
      { value: 'current-fy', label: `Current FY (${selectedFY})` },
      { value: 'custom', label: 'Custom Date Range' }
    ],
    months: fyMonths,
    quarters: [
      { value: 'Q1', label: 'Q1 (Apr-Jun)' },
      { value: 'Q2', label: 'Q2 (Jul-Sep)' },
      { value: 'Q3', label: 'Q3 (Oct-Dec)' },
      { value: 'Q4', label: 'Q4 (Jan-Mar)' }
    ]
  };
};

const LedgerPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const { selectedFY } = useFinancialYear();
  const [activeTab, setActiveTab] = useState('training-ledger');
  
  // Fund accounts state
  const [fundAccounts, setFundAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('OMNICARD');
  const [transactions, setTransactions] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Expense Ledger Summary state
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [ledgerFilters, setLedgerFilters] = useState({
    datePreset: '',
    selectedMonth: '',
    selectedQuarter: '',
    startDate: '',
    endDate: '',
    paymentStatuses: [],
    modesOfPayment: [],
    projectId: ''
  });
  const [paymentModes, setPaymentModes] = useState([]);
  
  // Project summary data
  const [projectSummary, setProjectSummary] = useState([]);
  const [rejectedStats, setRejectedStats] = useState({ count: 0, amount: 0 });
  
  // Expanded rows for drill-down
  const [expandedRows, setExpandedRows] = useState({});
  
  // Add funds modal
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [addFundsAccount, setAddFundsAccount] = useState('');
  const [addFundsAmount, setAddFundsAmount] = useState('');
  const [addFundsNote, setAddFundsNote] = useState('');
  const [addFundsDate, setAddFundsDate] = useState(new Date().toISOString().split('T')[0]);
  const [addingFunds, setAddingFunds] = useState(false);
  
  // Edit note modal
  const [editNoteOpen, setEditNoteOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editNoteValue, setEditNoteValue] = useState('');
  const [editDateValue, setEditDateValue] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Reverse confirmation
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [transactionToReverse, setTransactionToReverse] = useState(null);
  const [reversing, setReversing] = useState(false);
  
  // Legacy ledger state
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('date');
  const [filterProjectId, setFilterProjectId] = useState('');

  const canManageFunds = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';
  
  // Memoize date presets based on selected FY
  const datePresets = useMemo(() => getDatePresets(selectedFY), [selectedFY]);
  
  // Calculate date range from preset
  const calculateDateRange = useCallback((preset, month, quarter) => {
    const now = new Date();
    let startDate = '';
    let endDate = '';
    
    if (preset === 'current-month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    } else if (preset === 'previous-month') {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    } else if (preset === 'current-quarter') {
      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
      startDate = new Date(now.getFullYear(), quarterStart, 1).toISOString().split('T')[0];
      endDate = new Date(now.getFullYear(), quarterStart + 3, 0).toISOString().split('T')[0];
    } else if (preset === 'previous-quarter') {
      const quarterStart = Math.floor(now.getMonth() / 3) * 3 - 3;
      const year = quarterStart < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const adjustedQuarterStart = quarterStart < 0 ? quarterStart + 12 : quarterStart;
      startDate = new Date(year, adjustedQuarterStart, 1).toISOString().split('T')[0];
      endDate = new Date(year, adjustedQuarterStart + 3, 0).toISOString().split('T')[0];
    } else if (preset === 'current-fy' && selectedFY) {
      const fyStartYear = parseInt(selectedFY.split('-')[0]);
      startDate = `${fyStartYear}-04-01`;
      endDate = `${fyStartYear + 1}-03-31`;
    } else if (preset === 'month' && month) {
      // month format: "month-YYYY-MM"
      const [, year, monthNum] = month.split('-');
      startDate = `${year}-${monthNum}-01`;
      const lastDay = new Date(parseInt(year), parseInt(monthNum), 0).getDate();
      endDate = `${year}-${monthNum}-${String(lastDay).padStart(2, '0')}`;
    } else if (preset === 'quarter' && quarter && selectedFY) {
      const fyStartYear = parseInt(selectedFY.split('-')[0]);
      const quarterMap = {
        'Q1': { start: `${fyStartYear}-04-01`, end: `${fyStartYear}-06-30` },
        'Q2': { start: `${fyStartYear}-07-01`, end: `${fyStartYear}-09-30` },
        'Q3': { start: `${fyStartYear}-10-01`, end: `${fyStartYear}-12-31` },
        'Q4': { start: `${fyStartYear + 1}-01-01`, end: `${fyStartYear + 1}-03-31` }
      };
      if (quarterMap[quarter]) {
        startDate = quarterMap[quarter].start;
        endDate = quarterMap[quarter].end;
      }
    }
    
    return { startDate, endDate };
  }, [selectedFY]);
  
  // Toggle row expansion
  const toggleRowExpansion = (projectId) => {
    setExpandedRows(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Fetch payment modes
  const fetchPaymentModes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/master-lists`, {
        headers: getAuthHeaders()
      });
      const paymentModesList = response.data.find(list => list.list_type === 'payment_modes');
      if (paymentModesList) {
        setPaymentModes(paymentModesList.items.filter(item => item.is_active).map(item => item.value));
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch payment modes:', error);
    }
  }, [getAuthHeaders]);

  // Fetch fund accounts
  const fetchFundAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const response = await axios.get(`${API}/fund-accounts`, {
        headers: getAuthHeaders()
      });
      setFundAccounts(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch fund accounts:', error);
      toast.error('Failed to load fund accounts');
    } finally {
      setLoadingAccounts(false);
    }
  }, [getAuthHeaders]);

  // Fetch transactions for selected account
  const fetchTransactions = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      setLoadingTransactions(true);
      const response = await axios.get(`${API}/fund-accounts/${selectedAccount}/transactions`, {
        headers: getAuthHeaders()
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoadingTransactions(false);
    }
  }, [selectedAccount, getAuthHeaders]);

  // Fetch expenses for ledger view
  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', 100);
      if (filterProjectId) params.append('corporate_project_id', filterProjectId);

      const response = await axios.get(`${API}/expenses?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      const sortedExpenses = response.data.expenses.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setExpenses(sortedExpenses);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch expenses:', error);
      toast.error('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [filterProjectId, getAuthHeaders]);

  // Fetch ledger summary for Training and HR tabs
  const fetchLedgerSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      const params = new URLSearchParams();
      
      // Calculate date range from preset
      let startDate = ledgerFilters.startDate;
      let endDate = ledgerFilters.endDate;
      
      if (ledgerFilters.datePreset && ledgerFilters.datePreset !== 'custom') {
        if (ledgerFilters.datePreset.startsWith('month-')) {
          const range = calculateDateRange('month', ledgerFilters.datePreset, null);
          startDate = range.startDate;
          endDate = range.endDate;
        } else if (ledgerFilters.datePreset.startsWith('Q')) {
          const range = calculateDateRange('quarter', null, ledgerFilters.datePreset);
          startDate = range.startDate;
          endDate = range.endDate;
        } else {
          const range = calculateDateRange(ledgerFilters.datePreset, null, null);
          startDate = range.startDate;
          endDate = range.endDate;
        }
      }
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (ledgerFilters.paymentStatuses.length > 0) {
        params.append('payment_statuses', ledgerFilters.paymentStatuses.join(','));
      }
      if (ledgerFilters.modesOfPayment.length > 0) {
        params.append('modes_of_payment', ledgerFilters.modesOfPayment.join(','));
      }
      if (ledgerFilters.projectId) {
        params.append('project_id', ledgerFilters.projectId);
      }

      const response = await axios.get(`${API}/ledger/summary?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      setLedgerSummary(response.data);
      
      // Calculate project summary from response
      if (response.data.projects) {
        setProjectSummary(response.data.projects);
      }
      
      // Set rejected stats
      if (response.data.rejected) {
        setRejectedStats(response.data.rejected);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch ledger summary:', error);
      toast.error('Failed to load ledger summary');
    } finally {
      setLoadingSummary(false);
    }
  }, [ledgerFilters, calculateDateRange, getAuthHeaders]);

  useEffect(() => {
    fetchFundAccounts();
    fetchExpenses();
    fetchLedgerSummary();
    fetchPaymentModes();
  }, [fetchFundAccounts, fetchExpenses, fetchLedgerSummary, fetchPaymentModes]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleAddFunds = async () => {
    if (!addFundsAmount || parseFloat(addFundsAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setAddingFunds(true);
      await axios.post(`${API}/fund-accounts/${addFundsAccount}/credit`, {
        amount: parseFloat(addFundsAmount),
        date: addFundsDate,
        note: addFundsNote || `Funds added on ${addFundsDate}`
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(`₹${parseFloat(addFundsAmount).toLocaleString('en-IN')} added to ${addFundsAccount} account`);
      setAddFundsOpen(false);
      setAddFundsAmount('');
      setAddFundsNote('');
      fetchFundAccounts();
      if (selectedAccount === addFundsAccount) {
        fetchTransactions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add funds');
    } finally {
      setAddingFunds(false);
    }
  };

  const openAddFundsModal = (accountType) => {
    setAddFundsAccount(accountType);
    setAddFundsAmount('');
    setAddFundsNote('');
    setAddFundsDate(new Date().toISOString().split('T')[0]);
    setAddFundsOpen(true);
  };

  // Edit note handlers
  const openEditNoteModal = (txn) => {
    setEditingTransaction(txn);
    setEditNoteValue(txn.note || txn.expense_description || '');
    setEditDateValue(txn.date || '');
    setEditNoteOpen(true);
  };

  const handleSaveTransaction = async () => {
    if (!editingTransaction) return;
    try {
      setSavingNote(true);
      const updateData = { note: editNoteValue };
      
      // Only include date if user is Admin and date has changed
      if (user?.role === 'ADMIN' && editDateValue && editDateValue !== editingTransaction.date) {
        updateData.date = editDateValue;
      }
      
      await axios.patch(
        `${API}/fund-accounts/${selectedAccount}/transactions/${editingTransaction.id}`,
        updateData,
        { headers: getAuthHeaders() }
      );
      toast.success('Transaction updated');
      setEditNoteOpen(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setSavingNote(false);
    }
  };

  // Reverse transaction handler
  const handleReverse = async () => {
    if (!transactionToReverse) return;
    try {
      setReversing(true);
      const response = await axios.post(
        `${API}/fund-accounts/${selectedAccount}/transactions/${transactionToReverse.id}/reverse`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setReverseDialogOpen(false);
      setTransactionToReverse(null);
      fetchFundAccounts();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse transaction');
    } finally {
      setReversing(false);
    }
  };

  // Delete transaction handler
  const handleDelete = async () => {
    if (!transactionToDelete) return;
    try {
      setDeleting(true);
      const response = await axios.delete(
        `${API}/fund-accounts/${selectedAccount}/transactions/${transactionToDelete.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
      fetchFundAccounts();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Parse and format dates - handles various input formats
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Handle ISO format (2026-02-19 or 2026-02-19T...)
    if (dateStr.includes('-') && dateStr.length >= 10 && !isNaN(Date.parse(dateStr.substring(0, 10)))) {
      const parts = dateStr.substring(0, 10).split('-');
      if (parts[0].length === 4) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
      }
    }
    
    // Handle dd-Mon-yyyy format (19-Feb-2026)
    const monthMap = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const match = dateStr.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
    if (match) {
      const day = parseInt(match[1]);
      const month = monthMap[match[2]];
      const year = parseInt(match[3]);
      if (month !== undefined) {
        return new Date(year, month, day);
      }
    }
    
    return null;
  };

  const formatDateDisplay = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date || isNaN(date.getTime())) {
      return dateStr || '-';
    }
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getMonthYearFromDate = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date || isNaN(date.getTime())) {
      return 'Unknown';
    }
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  const getAccountIcon = (accountType) => {
    switch (accountType) {
      case 'ONLINE': return <Building2 className="w-5 h-5" />;
      case 'OMNICARD': return <CreditCard className="w-5 h-5" />;
      case 'DEPOSIT': return <Wallet className="w-5 h-5" />;
      default: return <Wallet className="w-5 h-5" />;
    }
  };

  const getAccountColor = (accountType) => {
    switch (accountType) {
      case 'ONLINE': return 'from-blue-600 to-blue-700';
      case 'OMNICARD': return 'from-violet-600 to-violet-700';
      case 'DEPOSIT': return 'from-emerald-600 to-emerald-700';
      default: return 'from-slate-600 to-slate-700';
    }
  };

  const getAccountBorderColor = (accountType) => {
    switch (accountType) {
      case 'ONLINE': return 'border-blue-200 bg-blue-50';
      case 'OMNICARD': return 'border-violet-200 bg-violet-50';
      case 'DEPOSIT': return 'border-emerald-200 bg-emerald-50';
      default: return 'border-slate-200 bg-slate-50';
    }
  };

  // Calculate running balance for ledger
  const calculateRunningBalance = () => {
    let balance = 0;
    return expenses.map(expense => {
      balance += expense.total_expenses || 0;
      return { ...expense, runningBalance: balance };
    }).reverse();
  };

  // Group expenses
  const groupExpenses = () => {
    const expensesWithBalance = calculateRunningBalance().reverse();
    
    if (groupBy === 'project') {
      const grouped = {};
      expensesWithBalance.forEach(expense => {
        const key = expense.corporate_project_id || 'Ad-hoc';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(expense);
      });
      return Object.entries(grouped);
    } else if (groupBy === 'type') {
      const grouped = {};
      expensesWithBalance.forEach(expense => {
        const key = expense.expense_type || 'Unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(expense);
      });
      return Object.entries(grouped);
    } else {
      const grouped = {};
      expensesWithBalance.forEach(expense => {
        const date = expense.reporting_date || expense.created_at?.split('T')[0];
        const monthYear = getMonthYearFromDate(date);
        if (!grouped[monthYear]) grouped[monthYear] = [];
        grouped[monthYear].push(expense);
      });
      return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
    }
  };

  const totalAmount = expenses.reduce((sum, exp) => sum + (exp.total_expenses || 0), 0);

  const handleExport = () => {
    const headers = ['Date', 'Type', 'Project ID', 'Description', 'Amount', 'Status'];
    const rows = expenses.map(exp => [
      exp.reporting_date || exp.created_at?.split('T')[0],
      exp.expense_type,
      exp.corporate_project_id || '',
      exp.training_name || exp.description || '',
      exp.total_expenses,
      exp.payment_info?.payment_status
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expense-ledger-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Ledger exported successfully');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { class: 'badge-pending', label: 'Pending' },
      APPROVED: { class: 'badge-approved', label: 'Approved' },
      PAID: { class: 'badge-paid', label: 'Paid' },
      REJECTED: { class: 'badge-rejected', label: 'Rejected' },
      ON_HOLD: { class: 'badge-on-hold', label: 'On Hold' }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <Badge className={`${config.class} font-mono text-xs`}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="ledger-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950 flex items-center gap-3">
            <BookOpen className="w-7 h-7" />
            Ledger & Reconciliation
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track fund balances and reconcile payments
          </p>
        </div>
        <Button onClick={handleExport} variant="outline" data-testid="export-btn">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="training-ledger" className="data-[state=active]:bg-white" data-testid="training-ledger-tab">
            <Building2 className="w-4 h-4 mr-2" />
            Training Ledger
          </TabsTrigger>
          <TabsTrigger value="hr-ledger" className="data-[state=active]:bg-white" data-testid="hr-ledger-tab">
            <BookOpen className="w-4 h-4 mr-2" />
            HR Ledger
          </TabsTrigger>
          <TabsTrigger value="reconciliation" className="data-[state=active]:bg-white" data-testid="reconciliation-tab">
            <RefreshCw className="w-4 h-4 mr-2" />
            Fund Reconciliation
          </TabsTrigger>
        </TabsList>

        {/* Training Ledger Tab */}
        <TabsContent value="training-ledger" className="mt-0 space-y-6">
          {/* Advanced Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="space-y-4">
                {/* Search and Filter Toggle Row */}
                <div className="flex flex-wrap gap-4 items-center">
                  {/* Project ID Search */}
                  <div className="flex-1 min-w-[200px] max-w-[300px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search Project ID (exact match)"
                        value={ledgerFilters.projectId}
                        onChange={(e) => setLedgerFilters({...ledgerFilters, projectId: e.target.value})}
                        onKeyDown={(e) => e.key === 'Enter' && fetchLedgerSummary()}
                        className="pl-10 h-9"
                        data-testid="project-id-search"
                      />
                    </div>
                  </div>

                  {/* Date Preset Dropdown */}
                  <div className="space-y-1">
                    <Select
                      value={ledgerFilters.datePreset || ""}
                      onValueChange={(val) => {
                        if (val.startsWith('month-')) {
                          setLedgerFilters({...ledgerFilters, datePreset: val, selectedMonth: val, selectedQuarter: ''});
                        } else if (val.startsWith('Q')) {
                          setLedgerFilters({...ledgerFilters, datePreset: val, selectedQuarter: val, selectedMonth: ''});
                        } else {
                          setLedgerFilters({...ledgerFilters, datePreset: val, selectedMonth: '', selectedQuarter: ''});
                        }
                      }}
                    >
                      <SelectTrigger className="h-9 w-48" data-testid="date-preset-select">
                        <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="Select Date Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="current-month">Current Month</SelectItem>
                        <SelectItem value="previous-month">Previous Month</SelectItem>
                        <SelectItem value="current-quarter">Current Quarter</SelectItem>
                        <SelectItem value="previous-quarter">Previous Quarter</SelectItem>
                        <SelectItem value="current-fy">Current FY ({selectedFY})</SelectItem>
                        {/* FY Quarters */}
                        <SelectItem value="Q1">Q1 (Apr-Jun)</SelectItem>
                        <SelectItem value="Q2">Q2 (Jul-Sep)</SelectItem>
                        <SelectItem value="Q3">Q3 (Oct-Dec)</SelectItem>
                        <SelectItem value="Q4">Q4 (Jan-Mar)</SelectItem>
                        {/* Specific Months */}
                        {datePresets.months.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                        <SelectItem value="custom">Custom Date Range</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom Date Range (only show if custom selected) */}
                  {ledgerFilters.datePreset === 'custom' && (
                    <>
                      <Input
                        type="date"
                        value={ledgerFilters.startDate}
                        onChange={(e) => setLedgerFilters({...ledgerFilters, startDate: e.target.value})}
                        className="h-9 w-36"
                        placeholder="Start Date"
                      />
                      <Input
                        type="date"
                        value={ledgerFilters.endDate}
                        onChange={(e) => setLedgerFilters({...ledgerFilters, endDate: e.target.value})}
                        className="h-9 w-36"
                        placeholder="End Date"
                      />
                    </>
                  )}

                  <Button
                    type="button"
                    variant={showFilters ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`h-9 ${showFilters ? "bg-indigo-600" : ""}`}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    More Filters
                    {(ledgerFilters.paymentStatuses.length > 0 || ledgerFilters.modesOfPayment.length > 0) && (
                      <Badge className="ml-2 bg-indigo-100 text-indigo-700">
                        {ledgerFilters.paymentStatuses.length + ledgerFilters.modesOfPayment.length}
                      </Badge>
                    )}
                  </Button>

                  <Button size="sm" onClick={fetchLedgerSummary} className="h-9 bg-indigo-600" data-testid="apply-ledger-filters">
                    Apply
                  </Button>

                  {(ledgerFilters.datePreset || ledgerFilters.projectId || ledgerFilters.paymentStatuses.length > 0 || ledgerFilters.modesOfPayment.length > 0) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setLedgerFilters({
                        datePreset: '',
                        selectedMonth: '',
                        selectedQuarter: '',
                        startDate: '',
                        endDate: '',
                        paymentStatuses: [],
                        modesOfPayment: [],
                        projectId: ''
                      })}
                      className="h-9 text-slate-500"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>

                {/* Expanded Multi-Select Filters */}
                {showFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-200">
                    {/* Payment Status Multi-select */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">Payment Status</Label>
                      <div className="flex flex-wrap gap-2">
                        {PAYMENT_STATUSES.map(status => (
                          <div key={status.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`ledger-status-${status.value}`}
                              checked={ledgerFilters.paymentStatuses.includes(status.value)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setLedgerFilters({...ledgerFilters, paymentStatuses: [...ledgerFilters.paymentStatuses, status.value]});
                                } else {
                                  setLedgerFilters({...ledgerFilters, paymentStatuses: ledgerFilters.paymentStatuses.filter(s => s !== status.value)});
                                }
                              }}
                            />
                            <label
                              htmlFor={`ledger-status-${status.value}`}
                              className="text-sm cursor-pointer"
                            >
                              <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Payment Mode Multi-select */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">Payment Mode</Label>
                      <div className="flex flex-wrap gap-2">
                        {paymentModes.map(mode => (
                          <div key={mode} className="flex items-center space-x-2">
                            <Checkbox
                              id={`ledger-mode-${mode}`}
                              checked={ledgerFilters.modesOfPayment.includes(mode)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setLedgerFilters({...ledgerFilters, modesOfPayment: [...ledgerFilters.modesOfPayment, mode]});
                                } else {
                                  setLedgerFilters({...ledgerFilters, modesOfPayment: ledgerFilters.modesOfPayment.filter(m => m !== mode)});
                                }
                              }}
                            />
                            <label htmlFor={`ledger-mode-${mode}`} className="text-sm cursor-pointer">
                              {mode}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rejected Card - Compact Inline */}
          {ledgerSummary?.rejected?.count > 0 && (
            <div 
              className="flex items-center gap-3 px-3 py-2 rounded-md border border-red-200 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors w-fit"
              onClick={() => navigate('/expenses?status=REJECTED')}
              data-testid="ledger-rejected-card"
            >
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs font-medium text-red-600">Rejected: {ledgerSummary.rejected.count}</span>
              <span className="text-xs font-mono text-red-700 font-semibold">{formatCurrency(ledgerSummary.rejected.amount)}</span>
            </div>
          )}

          {/* Summary Cards with Budget */}
          {ledgerSummary?.training && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Total Projects</p>
                  <p className="text-2xl font-bold font-mono text-indigo-950">{ledgerSummary.training.groups?.length || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <p className="text-xs text-blue-600">Total Budget</p>
                  <p className="text-2xl font-bold font-mono text-blue-700">{formatCurrency(ledgerSummary.training.total_budget || 0)}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Total Raised</p>
                  <p className="text-2xl font-bold font-mono text-indigo-950">{formatCurrency(ledgerSummary.training.total_amount)}</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4">
                  <p className="text-xs text-emerald-600">Total Paid</p>
                  <p className="text-2xl font-bold font-mono text-emerald-700">{formatCurrency(ledgerSummary.training.total_paid)}</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <p className="text-xs text-amber-600">Pending</p>
                  <p className="text-2xl font-bold font-mono text-amber-700">{formatCurrency(ledgerSummary.training.total_pending)}</p>
                </CardContent>
              </Card>
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <p className="text-xs text-purple-600">Remaining Budget</p>
                  <p className="text-2xl font-bold font-mono text-purple-700">{formatCurrency(ledgerSummary.training.total_remaining || 0)}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Training Groups Table */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950">Training Expenses by Zoho CRM ID</CardTitle>
              <CardDescription>Click on a row to see individual expenses</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSummary ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : ledgerSummary?.training?.groups?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No training expenses found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="w-8 p-3"></th>
                        <th className="text-left p-3 font-semibold text-slate-600">Project ID</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Client</th>
                        <th className="text-right p-3 font-semibold text-blue-600">Budget</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Raised</th>
                        <th className="text-right p-3 font-semibold text-emerald-600">Paid</th>
                        <th className="text-right p-3 font-semibold text-amber-600">Pending</th>
                        <th className="text-right p-3 font-semibold text-purple-600">Remaining</th>
                        <th className="text-center p-3 font-semibold text-slate-600">#</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerSummary?.training?.groups?.map((group, idx) => (
                        <React.Fragment key={group.project_id || `grp-${idx}`}>
                          {/* Main Row */}
                          <tr 
                            className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => toggleRowExpansion(group.project_id)}
                          >
                            <td className="p-3 text-center">
                              {expandedRows[group.project_id] ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </td>
                            <td className="p-3 font-mono text-indigo-600 font-semibold">{group.project_id}</td>
                            <td className="p-3 text-sm">{group.client_name || '-'}</td>
                            <td className="p-3 text-right font-mono text-blue-700 font-medium">{formatCurrency(group.budget || 0)}</td>
                            <td className="p-3 text-right font-mono">{formatCurrency(group.total_amount)}</td>
                            <td className="p-3 text-right font-mono text-emerald-600">{formatCurrency(group.paid_amount)}</td>
                            <td className="p-3 text-right font-mono text-amber-600">{formatCurrency(group.pending_amount)}</td>
                            <td className="p-3 text-right font-mono text-purple-600">{formatCurrency(group.remaining_budget || 0)}</td>
                            <td className="p-3 text-center">
                              <Badge variant="outline" className="text-xs">{group.count}</Badge>
                            </td>
                          </tr>
                          {/* Expanded Detail Rows */}
                          {expandedRows[group.project_id] && group.expenses?.map((expense, expIdx) => {
                            const expStatus = expense.payment_info?.payment_status || 'PENDING';
                            const isRejected = expStatus === 'REJECTED';
                            const expAmount = expense.payment_info?.amount_to_pay_inr || expense.total_expenses || 0;
                            const payments = expense.payments || [];
                            const paidSoFar = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
                            const pending = isRejected ? 0 : Math.max(0, expAmount - paidSoFar);
                            return (
                              <tr key={`${idx}-${expIdx}`} className={`border-b border-slate-100 ${isRejected ? 'bg-red-50/50' : 'bg-slate-50'}`}>
                                <td className="p-2"></td>
                                <td className="p-2 pl-4 text-xs text-slate-400">└</td>
                                <td className="p-2 text-sm" colSpan="2">
                                  {expense.training_name || '-'}
                                  <Badge 
                                    data-testid={`expense-status-badge-${expIdx}`}
                                    className={`ml-2 font-mono text-xs ${
                                      expStatus === 'PAID' ? 'bg-emerald-100 text-emerald-700' :
                                      expStatus === 'PARTIALLY_PAID' ? 'bg-blue-100 text-blue-700' :
                                      expStatus === 'APPROVED' ? 'bg-indigo-100 text-indigo-700' :
                                      expStatus === 'REJECTED' ? 'bg-red-100 text-red-700' :
                                      'bg-amber-100 text-amber-700'
                                    }`}
                                  >
                                    {expStatus}
                                  </Badge>
                                </td>
                                <td className="p-2 text-right font-mono text-sm">{formatCurrency(expAmount)}</td>
                                <td className="p-2 text-right font-mono text-sm text-emerald-600">{formatCurrency(paidSoFar)}</td>
                                <td className={`p-2 text-right font-mono text-sm ${isRejected ? 'text-slate-400 line-through' : 'text-amber-600'}`}>
                                  {isRejected ? '-' : formatCurrency(pending)}
                                </td>
                                <td className="p-2"></td>
                                <td className="p-2 text-center">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 text-xs text-indigo-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/expenses/${expense.id}`);
                                    }}
                                  >
                                    View
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                    {ledgerSummary?.training?.groups?.length > 0 && (
                      <tfoot className="bg-slate-100">
                        <tr>
                          <td className="p-3"></td>
                          <td colSpan="2" className="p-3 font-semibold">Total</td>
                          <td className="p-3 text-right font-mono font-bold text-blue-700">{formatCurrency(ledgerSummary.training.total_budget || 0)}</td>
                          <td className="p-3 text-right font-mono font-bold">{formatCurrency(ledgerSummary.training.total_amount)}</td>
                          <td className="p-3 text-right font-mono font-bold text-emerald-600">{formatCurrency(ledgerSummary.training.total_paid)}</td>
                          <td className="p-3 text-right font-mono font-bold text-amber-600">{formatCurrency(ledgerSummary.training.total_pending)}</td>
                          <td className="p-3 text-right font-mono font-bold text-purple-600">{formatCurrency(ledgerSummary.training.total_remaining || 0)}</td>
                          <td className="p-3"></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HR Ledger Tab */}
        <TabsContent value="hr-ledger" className="mt-0 space-y-6">
          {/* Filters (same as training) */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <Input
                    type="date"
                    value={ledgerFilters.startDate}
                    onChange={(e) => setLedgerFilters({...ledgerFilters, startDate: e.target.value})}
                    className="h-9 w-40"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">End Date</Label>
                  <Input
                    type="date"
                    value={ledgerFilters.endDate}
                    onChange={(e) => setLedgerFilters({...ledgerFilters, endDate: e.target.value})}
                    className="h-9 w-40"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Payment Status</Label>
                  <Select
                    value={ledgerFilters.paymentStatus || "all"}
                    onValueChange={(val) => setLedgerFilters({...ledgerFilters, paymentStatus: val === "all" ? "" : val})}
                  >
                    <SelectTrigger className="h-9 w-36">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="PARTIALLY_PAID">Partially Paid</SelectItem>
                      <SelectItem value="PAID">Paid</SelectItem>
                      <SelectItem value="REJECTED">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Payment Mode</Label>
                  <Select
                    value={ledgerFilters.modeOfPayment || "all"}
                    onValueChange={(val) => setLedgerFilters({...ledgerFilters, modeOfPayment: val === "all" ? "" : val})}
                  >
                    <SelectTrigger className="h-9 w-36">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="Omnicard">Omnicard</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Deposit">Deposit</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  onClick={fetchLedgerSummary}
                  className="h-9"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  Apply
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setLedgerFilters({ startDate: '', endDate: '', paymentStatus: '', modeOfPayment: '' });
                  }}
                  className="h-9"
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* HR Summary Cards */}
          {ledgerSummary?.hr && (
            <div className="grid grid-cols-4 gap-4">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Total Expenses</p>
                  <p className="text-2xl font-bold font-mono text-indigo-950">{ledgerSummary.hr.total_count || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Total Amount</p>
                  <p className="text-2xl font-bold font-mono text-indigo-950">{formatCurrency(ledgerSummary.hr.total_amount)}</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4">
                  <p className="text-xs text-emerald-600">Total Paid</p>
                  <p className="text-2xl font-bold font-mono text-emerald-700">{formatCurrency(ledgerSummary.hr.total_paid)}</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <p className="text-xs text-amber-600">Pending Payment</p>
                  <p className="text-2xl font-bold font-mono text-amber-700">{formatCurrency(ledgerSummary.hr.total_pending)}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* HR Expenses by Category */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950">HR Expenses by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSummary ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : ledgerSummary?.hr?.by_category?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No HR expenses found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left p-3 font-semibold text-slate-600">Category</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Payee Names</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Total Amount</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Paid Amount</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Pending</th>
                        <th className="text-center p-3 font-semibold text-slate-600">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerSummary?.hr?.by_category?.map((group, idx) => (
                        <tr key={group.category || `hr-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 font-medium">{group.category}</td>
                          <td className="p-3 text-xs text-slate-500">
                            {group.payee_names?.slice(0, 2).join(', ') || '-'}
                            {group.payee_names?.length > 2 && ` +${group.payee_names.length - 2}`}
                          </td>
                          <td className="p-3 text-right font-mono">{formatCurrency(group.total_amount)}</td>
                          <td className="p-3 text-right font-mono text-emerald-600">{formatCurrency(group.paid_amount)}</td>
                          <td className="p-3 text-right font-mono text-amber-600">{formatCurrency(group.pending_amount)}</td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className="font-mono">{group.count}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* HR Expenses by Submitted By */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950">HR Expenses by Submitted By</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSummary ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : ledgerSummary?.hr?.by_submitted_by?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No HR expenses found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left p-3 font-semibold text-slate-600">Submitted By</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Payee Names</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Total Amount</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Paid Amount</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Pending</th>
                        <th className="text-center p-3 font-semibold text-slate-600">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerSummary?.hr?.by_submitted_by?.map((group, idx) => (
                        <tr key={group.submitted_by || `sub-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 font-medium">{group.submitted_by}</td>
                          <td className="p-3 text-xs text-slate-500">
                            {group.payee_names?.slice(0, 2).join(', ') || '-'}
                            {group.payee_names?.length > 2 && ` +${group.payee_names.length - 2}`}
                          </td>
                          <td className="p-3 text-right font-mono">{formatCurrency(group.total_amount)}</td>
                          <td className="p-3 text-right font-mono text-emerald-600">{formatCurrency(group.paid_amount)}</td>
                          <td className="p-3 text-right font-mono text-amber-600">{formatCurrency(group.pending_amount)}</td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className="font-mono">{group.count}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reconciliation Tab */}
        <TabsContent value="reconciliation" className="mt-0 space-y-6">
          {/* Fund Account Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {loadingAccounts ? (
              Array(3).fill(0).map((_, i) => (
                <Card key={i} className="border-slate-200 animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-20 bg-slate-200 rounded"></div>
                  </CardContent>
                </Card>
              ))
            ) : (
              fundAccounts.map((account) => (
                <Card 
                  key={account.account_type} 
                  className={`border-2 cursor-pointer transition-all ${
                    selectedAccount === account.account_type 
                      ? getAccountBorderColor(account.account_type) + ' ring-2 ring-offset-2 ring-indigo-500' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedAccount(account.account_type)}
                  data-testid={`fund-card-${account.account_type.toLowerCase()}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${getAccountColor(account.account_type)} text-white`}>
                        {getAccountIcon(account.account_type)}
                      </div>
                      {canManageFunds && account.account_type !== 'ONLINE' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); openAddFundsModal(account.account_type); }}
                          data-testid={`add-funds-${account.account_type.toLowerCase()}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 font-medium">{account.account_type}</p>
                    <p className={`text-2xl font-bold font-mono mt-1 ${
                      account.current_balance < 0 ? 'text-red-600' : 'text-slate-900'
                    }`}>
                      {formatCurrency(account.current_balance)}
                      {account.current_balance < 0 && (
                        <AlertTriangle className="w-4 h-4 inline ml-2 text-red-500" />
                      )}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                        {formatCurrency(account.total_credits)}
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingDown className="w-3 h-3 text-red-500" />
                        {formatCurrency(account.total_debits)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Transaction History */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading text-indigo-950">
                    {selectedAccount} Transactions
                  </CardTitle>
                  <CardDescription>
                    {selectedAccount === 'OMNICARD' 
                      ? 'Includes UPI payments (uses Omnicard balance)' 
                      : selectedAccount === 'ONLINE'
                      ? 'Direct company account payments'
                      : 'Deposit account transactions'
                    }
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchTransactions}
                  data-testid="refresh-transactions"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingTransactions ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-950"></div>
                </div>
              ) : transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full data-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Description</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Balance</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">By</th>
                        {canManageFunds && (
                          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, index) => (
                        <tr 
                          key={txn.id} 
                          className={`border-b border-slate-100 hover:bg-slate-50 ${txn.reversed ? 'opacity-50' : ''}`}
                          data-testid={`transaction-row-${index}`}
                        >
                          <td className="py-3 px-4 font-mono text-sm text-slate-600">
                            {formatDateDisplay(txn.date)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Badge className={`text-xs ${
                                txn.transaction_type === 'CREDIT' 
                                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                                  : 'bg-red-100 text-red-700 border-red-200'
                              }`}>
                                {txn.transaction_type}
                              </Badge>
                              {txn.reversed && (
                                <Badge className="text-xs bg-slate-100 text-slate-500 border-slate-200">
                                  Reversed
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-900 max-w-[250px] truncate">
                            {txn.expense_description || txn.note || '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className={`flex items-center justify-end gap-1 font-mono text-sm font-semibold ${
                              txn.transaction_type === 'CREDIT' ? 'text-emerald-600' : 'text-red-600'
                            } ${txn.reversed ? 'line-through' : ''}`}>
                              {txn.transaction_type === 'CREDIT' ? (
                                <ArrowUpRight className="w-3 h-3" />
                              ) : (
                                <ArrowDownRight className="w-3 h-3" />
                              )}
                              {formatCurrency(txn.amount)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-indigo-950">
                            {formatCurrency(txn.balance_after)}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {txn.created_by || '-'}
                          </td>
                          {canManageFunds && (
                            <td className="py-3 px-4 text-right">
                              {!txn.reversed && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openEditNoteModal(txn)}>
                                      <Pencil className="w-4 h-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => { setTransactionToReverse(txn); setReverseDialogOpen(true); }}
                                    >
                                      <RotateCcw className="w-4 h-4 mr-2" />
                                      Reverse Entry
                                    </DropdownMenuItem>
                                    {user?.role === 'ADMIN' && !txn.expense_id && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                          className="text-red-600"
                                          onClick={() => { setTransactionToDelete(txn); setDeleteDialogOpen(true); }}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" />
                                          Delete
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">No transactions yet</h3>
                  <p className="text-slate-500 text-xs">
                    {canManageFunds && selectedAccount !== 'ONLINE' 
                      ? 'Click "Add" to add funds to this account'
                      : 'Transactions will appear here when expenses are paid'
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ledger Tab */}
        <TabsContent value="ledger" className="mt-0 space-y-4">
          {/* Summary Card */}
          <Card className="border-slate-200 bg-gradient-to-br from-indigo-950 to-indigo-900 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-200 text-sm">Total Ledger Value</p>
                  <p className="text-3xl font-bold font-mono mt-1">{formatCurrency(totalAmount)}</p>
                </div>
                <div className="text-right">
                  <p className="text-indigo-200 text-sm">Total Entries</p>
                  <p className="text-2xl font-bold font-mono mt-1">{expenses.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Group By</label>
                  <Select value={groupBy} onValueChange={setGroupBy}>
                    <SelectTrigger data-testid="group-by-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">By Month</SelectItem>
                      <SelectItem value="project">By Project ID</SelectItem>
                      <SelectItem value="type">By Expense Type</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Filter by Project</label>
                  <Input
                    placeholder="Project ID"
                    value={filterProjectId}
                    onChange={(e) => setFilterProjectId(e.target.value)}
                    data-testid="project-filter-input"
                  />
                </div>
                <Button variant="secondary" onClick={fetchExpenses} data-testid="apply-filter-btn">
                  <Filter className="w-4 h-4 mr-2" />
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Ledger Table */}
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
                </div>
              ) : expenses.length > 0 ? (
                <div className="space-y-6 p-6">
                  {groupExpenses().map(([group, items]) => (
                    <div key={group} className="space-y-2">
                      <div className="flex items-center justify-between sticky top-0 bg-slate-50 p-3 rounded-lg">
                        <h3 className="font-semibold text-indigo-950 font-heading">
                          {groupBy === 'date' ? new Date(group + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 
                           groupBy === 'type' ? group.replace('_', ' ') : 
                           `Project: ${group}`}
                        </h3>
                        <span className="font-mono text-sm text-slate-600">
                          {items.length} entries • {formatCurrency(items.reduce((sum, i) => sum + (i.total_expenses || 0), 0))}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full data-table">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Description</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Project</th>
                              <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Debit</th>
                              <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Balance</th>
                              <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((expense, index) => (
                              <tr key={expense.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-mono text-sm text-slate-600">
                                  {formatDateDisplay(expense.reporting_date || expense.created_at?.split('T')[0])}
                                </td>
                                <td className="py-2 px-3 text-sm text-slate-900">
                                  {expense.training_name || expense.description || expense.expense_type?.replace('_', ' ')}
                                </td>
                                <td className="py-2 px-3 font-mono text-sm text-slate-600">
                                  {expense.corporate_project_id || '-'}
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <span className="flex items-center justify-end gap-1 font-mono text-sm font-medium text-red-600">
                                    <ArrowDownRight className="w-3 h-3" />
                                    {formatCurrency(expense.total_expenses)}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-right font-mono text-sm font-semibold text-indigo-950">
                                  {formatCurrency(expense.runningBalance)}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {getStatusBadge(expense.payment_info?.payment_status)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-16 text-center">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-1">No entries found</h3>
                  <p className="text-slate-500 text-sm">The ledger is empty. Add expenses to see them here.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Funds Modal */}
      <Dialog open={addFundsOpen} onOpenChange={setAddFundsOpen}>
        <DialogContent className="sm:max-w-[425px]" data-testid="add-funds-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              Add Funds to {addFundsAccount}
            </DialogTitle>
            <DialogDescription>
              Enter the amount and details for the fund addition.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (₹) <span className="text-red-500">*</span></Label>
              <Input
                id="amount"
                type="number"
                min="1"
                value={addFundsAmount}
                onChange={(e) => setAddFundsAmount(e.target.value)}
                placeholder="Enter amount"
                data-testid="add-funds-amount"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date <span className="text-red-500">*</span></Label>
              <Input
                id="date"
                type="date"
                value={addFundsDate}
                onChange={(e) => setAddFundsDate(e.target.value)}
                data-testid="add-funds-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="note">Note (optional)</Label>
              <Textarea
                id="note"
                value={addFundsNote}
                onChange={(e) => setAddFundsNote(e.target.value)}
                placeholder="e.g., Monthly top-up, Transfer from main account"
                rows={2}
                data-testid="add-funds-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setAddFundsOpen(false)}
              data-testid="cancel-add-funds"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddFunds}
              disabled={!addFundsAmount || addingFunds}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-add-funds"
            >
              {addingFunds ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add Funds
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Modal */}
      <Dialog open={editNoteOpen} onOpenChange={setEditNoteOpen}>
        <DialogContent className="sm:max-w-[425px]" data-testid="edit-transaction-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-indigo-600" />
              Edit Transaction
            </DialogTitle>
            <DialogDescription>
              Update the details for this transaction.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Date field - only for Admin */}
            {user?.role === 'ADMIN' && (
              <div>
                <Label htmlFor="edit-date">Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editDateValue}
                  onChange={(e) => setEditDateValue(e.target.value)}
                  className="mt-2"
                  data-testid="edit-date-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="edit-note">Note</Label>
              <Textarea
                id="edit-note"
                value={editNoteValue}
                onChange={(e) => setEditNoteValue(e.target.value)}
                placeholder="Enter note or description"
                rows={3}
                className="mt-2"
                data-testid="edit-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditNoteOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTransaction}
              disabled={savingNote}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-transaction-btn"
            >
              {savingNote ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reverse Confirmation Dialog */}
      <AlertDialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new opposite entry to reverse this transaction. 
              The original entry will be marked as reversed.
              {transactionToReverse && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm"><strong>Amount:</strong> {formatCurrency(transactionToReverse.amount)}</p>
                  <p className="text-sm"><strong>Type:</strong> {transactionToReverse.transaction_type}</p>
                  <p className="text-sm"><strong>Note:</strong> {transactionToReverse.note || transactionToReverse.expense_description || '-'}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reversing}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReverse}
              disabled={reversing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {reversing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Reverse Entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone.
              The balance will be recalculated automatically.
              {transactionToDelete && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm"><strong>Amount:</strong> {formatCurrency(transactionToDelete.amount)}</p>
                  <p className="text-sm"><strong>Type:</strong> {transactionToDelete.transaction_type}</p>
                  <p className="text-sm"><strong>Note:</strong> {transactionToDelete.note || '-'}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LedgerPage;
